import { useState } from 'react';
import type React from 'react';
import { ChevronRightIcon, SearchIcon, XIcon } from '../Icons';
import type {
  BookshelfFilter,
  BookshelfSortOrder,
  BookshelfStatusFilter,
} from './useBookshelfFilters';

interface BookshelfFiltersProps {
  filter: BookshelfFilter;
  setFilter: (filter: BookshelfFilter) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  resultCount: number;
  selectedGenre: string;
  setSelectedGenre: (genre: string) => void;
  selectedTag: string;
  setSelectedTag: (tag: string) => void;
  selectedStatus: BookshelfStatusFilter;
  setSelectedStatus: (status: BookshelfStatusFilter) => void;
  sortOrder: BookshelfSortOrder;
  setSortOrder: (order: BookshelfSortOrder) => void;
  allGenres: string[];
  allTags: string[];
  resetSubFilters: () => void;
  isDark: boolean;
  borderCol: string;
}

const filterTabs: Array<{ value: BookshelfFilter; label: string }> = [
  { value: 'all', label: 'すべて' },
  { value: 'presets', label: '名作プリセット' },
  { value: 'my-novels', label: 'インポート' },
];

const statusOptions: Array<{ value: BookshelfStatusFilter; label: string }> = [
  { value: 'all', label: 'すべて' },
  { value: 'ongoing', label: '連載中' },
  { value: 'completed', label: '完結済' },
  { value: 'short', label: '短編' },
];

const chipBaseClass =
  'min-h-11 shrink-0 rounded-xl border px-3 text-xs font-semibold transition-colors active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500';
const chipRowClass =
  'flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto no-scrollbar py-0.5 scroll-smooth';
const chipLabelClass = 'w-12 shrink-0 text-xs font-semibold opacity-70';

export const BookshelfFilters: React.FC<BookshelfFiltersProps> = ({
  filter,
  setFilter,
  searchQuery,
  setSearchQuery,
  resultCount,
  selectedGenre,
  setSelectedGenre,
  selectedTag,
  setSelectedTag,
  selectedStatus,
  setSelectedStatus,
  sortOrder,
  setSortOrder,
  allGenres,
  allTags,
  resetSubFilters,
  isDark,
  borderCol,
}) => {
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const activeCount = [
    filter !== 'all',
    searchQuery.trim().length > 0,
    selectedGenre !== 'all',
    selectedTag !== 'all',
    selectedStatus !== 'all',
  ].filter(Boolean).length;

  const selectedChipClass = isDark
    ? 'border-amber-400 bg-amber-400/15 text-amber-200'
    : 'border-amber-600 bg-amber-50 text-amber-900';
  const idleChipClass = isDark
    ? 'border-white/10 bg-white/5 text-neutral-300 hover:bg-white/10'
    : 'border-black/10 bg-white text-neutral-700 hover:bg-black/5';

  const resetAllFilters = () => {
    setFilter('all');
    setSearchQuery('');
    resetSubFilters();
  };

  return (
    <section
      aria-label="本棚の検索と絞り込み"
      className={`rounded-2xl border p-3.5 sm:p-4 ${borderCol} ${
        isDark ? 'bg-[#121214]/90' : 'bg-white/90'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <div className="relative min-w-0 flex-1">
          <SearchIcon
            size={18}
            aria-hidden="true"
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 opacity-50"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="タイトル・作者を検索"
            aria-label="本棚を検索"
            className={`h-11 w-full rounded-xl border pl-10 pr-10 text-base outline-none transition-colors focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 ${
              isDark
                ? 'border-white/10 bg-black/20 text-neutral-100 placeholder:text-neutral-500'
                : 'border-black/10 bg-[#F7F6F3] text-neutral-900 placeholder:text-neutral-500'
            }`}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="検索をクリア"
              className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-r-xl opacity-60 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              <XIcon size={16} />
            </button>
          )}
        </div>
        <span
          aria-live="polite"
          className="w-12 shrink-0 text-right text-xs font-semibold opacity-65"
        >
          {resultCount}冊
        </span>
      </div>

      <fieldset className="mt-3 grid grid-cols-3 gap-2">
        <legend className="sr-only">本の種類</legend>
        {filterTabs.map((tab) => (
          <button
            type="button"
            key={tab.value}
            onClick={() => {
              setFilter(tab.value);
              resetSubFilters();
            }}
            className={`min-h-11 rounded-xl border px-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
              filter === tab.value ? selectedChipClass : idleChipClass
            }`}
            aria-pressed={filter === tab.value}
          >
            {tab.label}
          </button>
        ))}
      </fieldset>

      <button
        type="button"
        aria-expanded={isFiltersExpanded}
        aria-controls="bookshelf-extra-filters"
        onClick={() => setIsFiltersExpanded((expanded) => !expanded)}
        className={`mt-2 flex min-h-11 w-full items-center justify-between rounded-xl px-3 text-left text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
          isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'
        }`}
      >
        <span>絞り込み・並び替え</span>
        <span className="flex items-center gap-2">
          {activeCount > 0 && (
            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-bold text-white">
              {activeCount}
            </span>
          )}
          <ChevronRightIcon
            size={16}
            aria-hidden="true"
            className={`transition-transform ${isFiltersExpanded ? 'rotate-90' : ''}`}
          />
        </span>
      </button>

      {isFiltersExpanded && (
        <div
          id="bookshelf-extra-filters"
          className="mt-2 space-y-3 border-t border-current/10 pt-3"
        >
          <fieldset className={chipRowClass}>
            <legend className="sr-only">読書状況</legend>
            <span className={chipLabelClass} aria-hidden="true">
              状況
            </span>
            {statusOptions.map((status) => (
              <button
                type="button"
                key={status.value}
                onClick={() => setSelectedStatus(status.value)}
                className={`${chipBaseClass} ${
                  selectedStatus === status.value ? selectedChipClass : idleChipClass
                }`}
                aria-pressed={selectedStatus === status.value}
              >
                {status.label}
              </button>
            ))}
          </fieldset>

          {allGenres.length > 1 && (
            <fieldset className={chipRowClass}>
              <legend className="sr-only">ジャンル</legend>
              <span className={chipLabelClass} aria-hidden="true">
                ジャンル
              </span>
              {allGenres.map((genre) => (
                <button
                  type="button"
                  key={genre}
                  onClick={() => setSelectedGenre(genre)}
                  className={`${chipBaseClass} ${
                    selectedGenre === genre ? selectedChipClass : idleChipClass
                  }`}
                  aria-pressed={selectedGenre === genre}
                >
                  {genre === 'all' ? 'すべて' : genre}
                </button>
              ))}
            </fieldset>
          )}

          {allTags.length > 1 && (
            <fieldset className={chipRowClass}>
              <legend className="sr-only">タグ</legend>
              <span className={chipLabelClass} aria-hidden="true">
                タグ
              </span>
              {allTags.map((tag) => (
                <button
                  type="button"
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`${chipBaseClass} ${
                    selectedTag === tag ? selectedChipClass : idleChipClass
                  }`}
                  aria-pressed={selectedTag === tag}
                >
                  {tag === 'all' ? 'すべて' : `#${tag}`}
                </button>
              ))}
            </fieldset>
          )}

          <fieldset className={chipRowClass}>
            <legend className="sr-only">並び順</legend>
            <span className={chipLabelClass} aria-hidden="true">
              並び順
            </span>
            {(
              [
                { value: 'recent', label: '最近追加' },
                { value: 'title', label: 'タイトル順' },
                { value: 'manual', label: '手動順' },
              ] as const
            ).map((option) => (
              <button
                type="button"
                key={option.value}
                onClick={() => setSortOrder(option.value)}
                className={`${chipBaseClass} ${
                  sortOrder === option.value ? selectedChipClass : idleChipClass
                }`}
                aria-pressed={sortOrder === option.value}
              >
                {option.label}
              </button>
            ))}
          </fieldset>

          {activeCount > 0 && (
            <button
              type="button"
              aria-label="リセット"
              onClick={resetAllFilters}
              className={`min-h-11 w-full rounded-xl border px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                isDark
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                  : 'border-amber-600/30 bg-amber-50 text-amber-900'
              }`}
            >
              条件をすべて解除
            </button>
          )}
        </div>
      )}
    </section>
  );
};
